import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Space, Typography, message, Flex, Divider } from "antd";
import { Button } from "@libertify/ui";
import type { UseMutationResult } from "@tanstack/react-query";
import type {
  MfaFactor,
  MfaFactorStatus,
  TotpEnrollmentResponse,
} from "../types";
import { getMfaErrorDetail } from "../utils";
import { DisableFactorButton } from "./DisableFactorButton";
import { MfaOtpInput } from "./MfaOtpInput";

export interface TotpFactorCardProps {
  factor: MfaFactorStatus;
  startTotp: UseMutationResult<TotpEnrollmentResponse, unknown, void>;
  confirmTotp: UseMutationResult<unknown, unknown, string>;
  disableFactor: UseMutationResult<unknown, unknown, MfaFactor>;
  // Called after a successful verify/disable so the parent collapse panel closes.
  onCollapse: () => void;
}

// "idle" (Set up button) -> "verify" (QR code + secret + Input.OTP)
type Step = "idle" | "verify";

export const TotpFactorCard = ({
  factor,
  startTotp,
  confirmTotp,
  disableFactor,
  onCollapse,
}: TotpFactorCardProps) => {
  const t = useTranslate();
  const [step, setStep] = useState<Step>("idle");
  const [enrollment, setEnrollment] = useState<TotpEnrollmentResponse | null>(
    null
  );
  const [codeError, setCodeError] = useState<string | null>(null);

  const handleSetUp = () => {
    startTotp.mutate(undefined, {
      onSuccess: (data) => {
        setEnrollment(data);
        setStep("verify");
      },
      onError: (error) => {
        console.error("Failed to start TOTP enrollment:", error);
        message.error(t("pages.settings.security.genericError"));
      },
    });
  };

  const handleVerify = (code: string) => {
    setCodeError(null);
    confirmTotp.mutate(code, {
      onSuccess: () => {
        setStep("idle");
        setEnrollment(null);
        onCollapse();
      },
      onError: (error) => {
        setCodeError(
          getMfaErrorDetail(error)?.message ||
            t("pages.settings.security.invalidCode")
        );
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        {t("pages.settings.security.factors.Totp.description")}
      </Typography.Text>

      {factor.enabled && (
        <DisableFactorButton
          factor="Totp"
          disableFactor={disableFactor}
          onCollapse={onCollapse}
        />
      )}

      {!factor.enabled && step === "idle" && (
        <Button
          variant="primary"
          onClick={handleSetUp}
          loading={startTotp.isPending}
        >
          {t("pages.settings.security.setUp")}
        </Button>
      )}

      {!factor.enabled && step === "verify" && enrollment && (
        <Flex vertical gap={10} align="center">
          <Typography.Text strong>
            {t("pages.settings.security.factors.Totp.scanQrCode")}
          </Typography.Text>
          <img
            src={`data:image/png;base64,${enrollment.qrcode}`}
            alt={t("pages.settings.security.factors.Totp.qrCodeAlt")}
            width={180}
            height={180}
          />
          <Typography.Text strong>
            {t("pages.settings.security.factors.Totp.secretFallback")}
          </Typography.Text>
          <Typography.Text code copyable>
            {enrollment.secret_code}
          </Typography.Text>

          <Divider />
          <MfaOtpInput
            promptText={t("pages.settings.security.factors.Totp.enterCode")}
            error={codeError}
            isPending={confirmTotp.isPending}
            onComplete={handleVerify}
          />
        </Flex>
      )}
    </Space>
  );
};
